import { useMemo, useCallback } from "react";
import { FlatList } from "react-native";
import { useRouter } from "expo-router";
import { useUnistyles, UnistylesRuntime } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { CameraListItem, useCamerasListState } from "@src/features/cameras";
import { getCameraKeyString, getCameraSourceAndFleet } from "@src/utils/key-parser";
import { sizes } from "@src/constants/sizes";
import type { SortState } from "@src/features/clips/types/camera";
import { SearchAndSortBar } from "./SearchAndSortBar.web";
import { useAmplitude } from "@src/hooks/useAmplitude";
import { ANALYTICS_EVENTS } from "@src/services/analytics/events";
import type { CameraApiResponse } from "@src/services/api/cameras-api";
import { useCameraCollection, buildGroupedGridItems } from "@src/features/cameras/hooks/useCameraCollection";
import { useSiteNames } from "@src/features/cameras/hooks/useSiteNames";
import { useSnapshots } from "@visioni/a3s-media/app";

interface CameraGridProps {
  siteId?: string;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  sortBy: SortState;
  onSortChange: (sortBy: SortState) => void;
}

export function CameraGrid({ siteId, searchQuery, onSearchChange, sortBy, onSortChange }: CameraGridProps) {
  const router = useRouter();
  const { theme } = useUnistyles();
  const { track } = useAmplitude();
  const listState = useCamerasListState(siteId);
  const { data: snapshots } = useSnapshots({ refetchInterval: 30000 });
  const siteNames = useSiteNames();

  const columnCount = useMemo(
    () => ({
      xs: 1,
      sm: 2,
      md: 3,
      lg: 3,
      xl: 3,
    }),
    [],
  );
  const currentBreakpoint = UnistylesRuntime.breakpoint as keyof typeof columnCount;
  const numColumns = columnCount[currentBreakpoint] || columnCount.xs;
  const gridSpacing = theme.spacing["6xl"];

  const { sortedCameras } = useCameraCollection({
    cameras: listState.status === "success" ? listState.cameras : undefined,
    searchQuery,
    sortBy,
    siteNames,
  });
  const showTitle = !!siteId;

  const listItems = useMemo(() => {
    return buildGroupedGridItems(sortedCameras, numColumns, snapshots);
  }, [sortedCameras, numColumns, snapshots]);

  const handleCameraSelect = useCallback(
    (camera: CameraApiResponse) => {
      const cameraKey = getCameraKeyString(camera);
      // Use the camera's actual site ID from the camera data, not the prop
      const cameraSiteId = camera.site.id;
      const cameraTenantId = camera.site.tenant;

      // Track camera click analytics
      const sourceAndFleet = getCameraSourceAndFleet(camera);
      track(ANALYTICS_EVENTS.CAMERA_CLICKED, {
        camera_id: cameraKey,
        ...(sourceAndFleet && {
          source: sourceAndFleet.source,
          generation: sourceAndFleet.fleet,
        }),
        tenant_id: cameraTenantId,
        site_id: cameraSiteId,
      });

      router.push(`/tenants/${cameraTenantId}/sites/${cameraSiteId}/cameras/${cameraKey}` as never);
    },
    [router, track],
  );

  const renderCameraItem = useCallback(
    (listItem: { item: (typeof listItems)[number] } | undefined) => {
      if (!listItem?.item) {
        return (
          <View
            style={{
              width: `${100 / numColumns}%`,
              paddingHorizontal: gridSpacing / 2,
              paddingVertical: gridSpacing / 2,
            }}
          />
        );
      }
      const { camera, snapshot } = listItem.item;
      return (
        <View
          style={{
            width: `${100 / numColumns}%`,
            paddingHorizontal: gridSpacing / 2,
            paddingVertical: gridSpacing / 2,
          }}
        >
          <CameraListItem
            camera={camera}
            siteId={camera.site.id}
            onSelect={() => handleCameraSelect(camera)}
            href={
              `/tenants/${camera.site.tenant}/sites/${camera.site.id}/cameras/${encodeURIComponent(getCameraKeyString(camera))}` as never
            }
            snapshot={snapshot}
          />
        </View>
      );
    },
    [gridSpacing, handleCameraSelect, numColumns],
  );

  if (listState.status === "pending") {
    return (
      <View flex={1} style={{ padding: theme.spacing["6xl"] }}>
        <View
          width="100%"
          maxWidth={sizes.maxWidthWeb}
          gap={showTitle ? "6xl" : undefined}
          pb="6xl"
          style={{ alignSelf: "center", zIndex: 10 }}
        >
          {showTitle && (
            <Text textStyle="lg/bold" color="text">
              {siteId} Cameras
            </Text>
          )}
          <SearchAndSortBar
            searchQuery={searchQuery}
            onSearchChange={onSearchChange}
            sortBy={sortBy}
            onSortChange={onSortChange}
            isLoading
          />
        </View>
      </View>
    );
  }

  if (listState.status === "error") {
    return (
      <View flex={1} justifyContent="center" alignItems="center">
        <Text textStyle="md/regular" color="textMuted">
          Failed to load cameras
        </Text>
      </View>
    );
  }

  return (
    <View flex={1} style={{ padding: theme.spacing["6xl"] }}>
      {/* Fixed Header Section - Title and Search/Sort Bar */}
      <View
        width="100%"
        maxWidth={sizes.maxWidthWeb}
        gap={showTitle ? "6xl" : undefined}
        pb="6xl"
        style={{ alignSelf: "center", zIndex: 10 }}
      >
        {showTitle && (
          <Text textStyle="lg/bold" color="text">
            {siteId} Cameras
          </Text>
        )}

        {/* Search and Sort Bar - Outside scroll container */}
        <SearchAndSortBar
          searchQuery={searchQuery}
          onSearchChange={onSearchChange}
          sortBy={sortBy}
          onSortChange={onSortChange}
        />
      </View>

      {/* Scrollable Camera Grid */}
      <View flex={1} alignItems="center">
        {listItems.length === 0 ?
          <View flex={1} justifyContent="center" alignItems="center" py="13xl">
            <Text textStyle="md/regular" color="textMuted">
              {searchQuery ? "No cameras found" : "No cameras available"}
            </Text>
          </View>
        : <FlatList
            key={numColumns}
            data={listItems}
            renderItem={renderCameraItem}
            keyExtractor={(item, index) => (item ? getCameraKeyString(item.camera) : index.toString())}
            numColumns={numColumns}
            showsVerticalScrollIndicator={false}
            windowSize={5}
            initialNumToRender={numColumns * 4}
            removeClippedSubviews
            style={{ width: "100%", maxWidth: sizes.maxWidthWeb }}
            contentContainerStyle={{ paddingHorizontal: gridSpacing / 2, paddingBottom: theme.spacing["8xl"] }}
          />
        }
      </View>
    </View>
  );
}
